import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormikProps } from 'formik';
import React, { useMemo } from 'react';
import { currencyList } from '../../../../graphql/projects/models/Currencies';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';
import VoxelInput from '../../../voxel/Forms/VoxelInput';
import VoxelSelect from '../../../voxel/Forms/VoxelSelect';
import VoxelText from '../../../voxel/Text/VoxelText';
import VoxelAutocompleteCity from '../../Autocomplete/VoxelAutocompleteCity';
import VoxelAutocompleteCountry, {
  globalOption,
} from '../../Autocomplete/VoxelAutocompleteCountry';
import VoxelAutocompleteLanguage from '../../Autocomplete/VoxelAutocompleteLanguage';
import DomainForm from '../DomainForm';
import { TranslateFormState } from './TranslateFormState';

export interface TranslateProjectFormProps {
  formBag: FormikProps<TranslateFormState>;
  user: CurrentUserGq;
}

const TranslateProjectForm: React.FC<TranslateProjectFormProps> = ({ formBag, user }) => {
  const showRankSection = useMemo(
    () => formBag.values.newCountry?.id === globalOption.id,
    [formBag.values.newCountry]
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="w-full flex flex-row gap-6">
        <div className="grow">
          <VoxelInput
            id={'projectName'}
            label="Project name *"
            labelPosition="top"
            placeholder="Example project"
            value={formBag.values.newName}
            onChange={(event) => formBag.setFieldValue('newName', event.target.value)}
            deleteValue={() => formBag.setFieldValue('newName', '')}
            helperText={formBag.errors?.newName}
            showHelperText={formBag.errors?.newName?.length > 0 && formBag.submitCount > 0}
          />
        </div>
        <div className="grow-0">
          <VoxelSelect
            id="currency"
            label="Currency *"
            labelPosition="top"
            options={currencyList.map((currency) => ({
              title: currency.code + ' (' + currency.symbol + ')',
              value: currency.code,
            }))}
            selectedOptionValue={formBag.values?.newCurrency?.code}
            helperText={formBag.errors?.newCurrency as any}
            showHelperText={!!(formBag.errors?.newCurrency as any)?.length}
            setSelectedOption={(option) =>
              formBag.setFieldValue(
                'newCurrency',
                currencyList.find((currency) => currency.code === option.value)
              )
            }
            disabled={formBag.isSubmitting}
            inputWidthClass="w-32"
          />
        </div>
      </div>
      <div className="">
        <VoxelAutocompleteLanguage
          type="user"
          labelPosition="top"
          preText={
            <div className="w-36 flex flex-row items-center justify-between flex-nowrap ml-1 mr-4">
              <VoxelText color={!formBag.values.fromLanguage ? 'error' : 'dark'}>
                {formBag.values.fromLanguage?.name ?? 'All languages'}
              </VoxelText>
              <div>
                <FontAwesomeIcon icon={faArrowRight} size="sm" />
              </div>
            </div>
          }
          selectedLanguage={formBag.values.newLanguage}
          setSelectedLanguage={(language) => formBag.setFieldValue('newLanguage', language)}
          disabled={formBag.isSubmitting}
        />
      </div>
      <div className="">
        <VoxelAutocompleteCountry
          type="both"
          labelPosition="top"
          preText={
            <div className="w-36 flex flex-row items-center justify-between flex-nowrap ml-1 mr-4">
              <VoxelText>{formBag.values.fromCountry?.name ?? 'Global'}</VoxelText>
              <div>
                <FontAwesomeIcon icon={faArrowRight} size="sm" />
              </div>
            </div>
          }
          selectedCountry={formBag.values.newCountry}
          setSelectedCountry={(location) => formBag.setFieldValue('newCountry', location)}
          disabled={formBag.isSubmitting}
        />
      </div>
      <div className="">
        <VoxelAutocompleteCity
          type="keyword"
          labelPosition="top"
          preText={
            <div className="w-36 flex flex-row items-center justify-between flex-nowrap ml-1 mr-4">
              <VoxelText>
                {formBag.values.fromCity?.name ? formBag.values.fromCity?.name : 'none'}
              </VoxelText>
              <div>
                <FontAwesomeIcon icon={faArrowRight} size="sm" />
              </div>
            </div>
          }
          selectedCountry={formBag.values.newCity}
          setSelectedCity={(location) => formBag.setFieldValue('newCity', location)}
          disabled={formBag.isSubmitting || !formBag.values.newCountry}
        />
      </div>
      {showRankSection && (
        <div className="flex flex-col gap-4 rounded-lg -m-3 p-3 -mt-2 pt-2 -mb-4 pb-4  bg-voxel-blue-50 ">
          <VoxelText variant="bread-medium">
            Rank data must have a specified language and location
          </VoxelText>
          <div className="flex flex-row gap-x-4">
            <div className="basis-1/2">
              <VoxelAutocompleteCountry
                type="rank"
                labelPosition="top"
                selectedCountry={formBag.values.serpCountry}
                setSelectedCountry={(country) => formBag.setFieldValue('serpCountry', country)}
                clearSelectedCity={() => formBag.setFieldValue('serpCity', null)}
                disabled={formBag.isSubmitting}
                outlined
              />
            </div>
            <div className="basis-1/2">
              <VoxelAutocompleteCity
                type="rank"
                labelPosition="top"
                selectedCountry={formBag.values.serpCountry}
                selectedCity={formBag.values.serpCity}
                setSelectedCity={(city) => formBag.setFieldValue('serpCity', city)}
                disabled={formBag.isSubmitting || !formBag.values.serpCountry}
                outlined
              />
            </div>
          </div>
        </div>
      )}
      <div className="w-full flex flex-col gap-4">
        <VoxelText>Project domain</VoxelText>
        <DomainForm
          protocolValue={formBag.values.newDomain.protocol}
          setProtocolValue={(value) =>
            formBag.setValues((prev) => {
              return { ...prev, newDomain: { ...prev.newDomain, protocol: value } };
            })
          }
          domainValue={formBag.values.newDomain.domain}
          setDomainFormState={(valueFunc) =>
            formBag.setValues((prev) => {
              return { ...prev, newDomain: valueFunc(prev.newDomain) };
            })
          }
          deleteDomainValue={() =>
            formBag.setValues((prev) => {
              return { ...prev, newDomain: { ...prev.newDomain, domain: '' } };
            })
          }
          disabled={formBag.isSubmitting}
          placeholder={'www.organization.com/se-sv'}
          helperText={formBag.errors?.newDomain?.domain}
          showHelperText={formBag.errors?.newDomain?.domain?.length > 0 && formBag.submitCount > 0}
        />
      </div>
    </div>
  );
};

export default TranslateProjectForm;
